//librerías
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useNavigate } from "react-router-dom";

//context
import { AuthContext } from "../../../../context/AuthContext";

//componentes
import { Button, Container, Spinner } from "react-bootstrap";
import {
  faCarSide,
  faPen,
  faSquarePlus,
  faToggleOff,
  faToggleOn,
} from "@fortawesome/free-solid-svg-icons";

//interfaces
import { ClientesInterface } from "../../../../interfaces/ClientesInterface";

//hooks
import useFetch from "../../../../hooks/useFetch";
import { ColumnDef } from "@tanstack/react-table";
import CustomTable from "../../../Formularios/CustomTable/CustomTable";
import ForbiddenMessage from "../../../Mensajes/ForbiddenMessage";
import { toast } from "react-toastify";

//estilos
const classes = require("./ClientesTable.module.css").default;

const ClientesTable = () => {
  const authContext = useContext(AuthContext);
  //variables de estado
  const [clientes, setClientes] = useState<ClientesInterface[]>();
  const { sendRequest, error, setError } = useFetch();
  const navigate = useNavigate();

  //Función que trata los clientes traídos de la BD.
  const transformData = useCallback((variable: any) => {
    setClientes(variable.data);
  }, []);

  //Función que llama todos los clientes paginados
  useEffect(() => {
    sendRequest(
      {
        url: "/cliente",
      },
      transformData
    );
  }, [sendRequest, transformData]);

  //Función que desactiva a un usuario.
  const changeClienteStatus = useCallback(
    (idCliente: number) => {
      if (
        window.confirm(
          "¿Seguro que desea cambiar el estatus del cliente? No podrá agregarle servicios"
        )
      ) {
        sendRequest(
          {
            url: `/cliente/${idCliente}`,
            method: "DELETE",
          },
          (variable: any) => {
            setClientes([]);
            toast.success("El cliente ha cambiado de estatus.", {
              position: "top-left",
              autoClose: 5000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
              progress: undefined,
              theme: "light",
            });
            sendRequest(
              {
                url: "/cliente",
              },
              transformData
            );
          }
        );
      }
    },
    [sendRequest, transformData]
  );

  const columns = useMemo<ColumnDef<ClientesInterface>[]>(
    () => [
      {
        header: "#",
        accessorKey: "id",
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id,
        enableColumnFilter: false,
      },
      {
        header: "Razón social",
        accessorFn: (row) => row.razon_social,
        id: "Correo",
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id,
      },
      {
        accessorKey: "nombre_comercial",
        header: () => "Nombre comercial",
        footer: (props) => props.column.id,
      },
      {
        header: "Estatus",
        cell: (info) => info.getValue(),
        accessorFn: (row) => (row.estatus ? "Activo" : "Inactivo"),
      },
      {
        header: "Acciones",
        cell: (info) => (
          <div className="btn-group" role="group" aria-label="Basic example">
            <Button
              title="Editar cliente"
              style={{ backgroundColor: "#2874A6", borderColor: "white" }}
              onClick={() =>
                navigate(
                  `/main/catalogos/clientes/edit/${info.row.original.id}`
                )
              }
              disabled={!authContext.tokenCan("Cliente:update")}
            >
              <FontAwesomeIcon icon={faPen} />
            </Button>
            {/** Botón para administrar unidades*/}
            <Button
              title="Administrar unidades"
              style={{ backgroundColor: "#2874A6", borderColor: "white" }}
              onClick={() =>
                navigate(
                  `/main/catalogos/unidades/listar/${info.row.original.id}`
                )
              }
              disabled={!authContext.tokenCan("Unidad:administrar")}
            >
              <FontAwesomeIcon icon={faCarSide} />
            </Button>
            {/** Botón para ver eliminar*/}
            <Button
              title={
                info.row.original.estatus
                  ? "Deshabilitar cliente"
                  : "Habilitar cliente"
              }
              style={{ backgroundColor: "#0d5475", borderColor: "white" }}
              onClick={() => changeClienteStatus(info.row.original.id)}
            >
              <FontAwesomeIcon
                icon={info.row.original.estatus ? faToggleOn : faToggleOff}
              />
            </Button>
          </div>
        ),
        footer: (props) => props.column.id,
      },
    ],
    [changeClienteStatus, navigate, authContext]
  );

  //Mientras se carga el contenido se muestra un spinner
  if (!clientes) {
    return (
      <div className={classes.spinnerContainer}>
        <Spinner animation="grow" variant="info" />
      </div>
    );
  }

  return (
    <Container>
      <h2>Lista de clientes</h2>
      {/** Botón para agregar usuario */}
      {authContext.tokenCan("Cliente:store") && (
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            title="Agregar un nuevo cliente"
            style={{
              backgroundColor: "#21618C",
              borderColor: "white",
              marginBottom: "10px",
            }}
            onClick={() => navigate(`/main/catalogos/clientes/crear`)}
          >
            <FontAwesomeIcon icon={faSquarePlus} /> Nuevo cliente
          </Button>
        </div>
      )}

      {/** Alerta de permisos */}
      {error === 403 && (
        <ForbiddenMessage onCloseHandler={() => setError(null)} />
      )}
      <CustomTable
        {...{
          data: clientes,
          columns,
        }}
      />
    </Container>
  );
};

export default ClientesTable;
